import { useState, useContext, useMemo, useEffect, useRef } from 'react';

import { BasicRound } from '@lcsc-design/utils/decimal';
import SvgIcon from '@lcsc-ui/components/SvgIcon';
import { LayoutContext } from '@lcsc-ui/layouts/baseLayout/provider';
import { buriedPoint } from '@lcsc-ui/utils/buriedPoint';
import sendAxios from '@lcsc-ui/utils/request';
import { message } from 'antd';

import API from '@/apis/index';
import GoBuyDialog from '@/components/goBuyDialog';
import Image from '@/components/Image';
import { defaultImg } from '@/constants';

export default function ProductRecommend({ data, setProductRecommendMounted }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [productData, setProductData] = useState('');
  const [type, setType] = useState('electronic');
  const [buyRow, setBuyRow] = useState({});
  const { resourceData } = useContext(LayoutContext);
  const { projectConfigVO } = resourceData;

  const [mroRecommend, setMroRecommend] = useState(null);
  const [productRelationRecommend, setProductRelationRecommend] = useState(null);
  const [productRecommendLoading, setProductRecommendLoading] = useState(true);

  const productRecommendRef = useRef(null);
  const mroProductRecommendRef = useRef(null);

  useEffect(() => {
    async function fetchData() {
      await Promise.all([getMroRecommend(), getProductRelationRecommend()]);
      setProductRecommendLoading(false);
    }
    fetchData();
  }, []);

  // 判断组件是否已挂载
  useEffect(() => {
    if (!productRecommendRef.current && !mroProductRecommendRef.current) return;
    setProductRecommendMounted(true);
  }, [productRecommendLoading, mroRecommend, productRelationRecommend]);

  const getMroRecommend = () => {
    sendAxios({
      method: 'get',
      url: `${API.mroRecommend}/${data?.productRecord?.productId}`,
      baseURL: 'lcscItemUrl',
    }).then((res) => {
      if (res?.code === 200) {
        setMroRecommend(res?.result);
      }
    });
  };

  const getProductRelationRecommend = () => {
    sendAxios({
      method: 'get',
      url: `${API.productRelationRecommend}?productId=${data?.productRecord?.productId}`,
      baseURL: 'lcscItemUrl',
    }).then((res) => {
      if (res?.code === 200) {
        setProductRelationRecommend(res?.result);
      }
    });
  };

  const mroRecommendList = useMemo(() => {
    let list = [];
    if (mroRecommend?.isCompatibleOld) {
      list = mroRecommend?.oldRecommendList || [];
    } else {
      list = mroRecommend?.recommendList || [];
    }
    return list.slice(0, 8);
  }, [mroRecommend]);

  const recommendList = useMemo(() => {
    if (productRelationRecommend?.productConnectorVO?.productList?.length) {
      // 配套连接器
      return productRelationRecommend.productConnectorVO.productList.map((item) => ({
        ...(item?.productSimpleVO || {}),
        connectorConfigDetailName: item?.connectorConfigDetailName,
        recommendType: 'connector',
      }));
    } else if (productRelationRecommend?.associateVOList?.length) {
      // 关联推荐
      return productRelationRecommend.associateVOList;
    } else {
      return [];
    }
  }, [productRelationRecommend]);

  const clickGoBuy = (row, type) => {
    let dataObj = {};
    sendAxios({
      method: 'get',
      url: `${API.checkStatus}?productId=${row.productId}`,
      baseURL: 'lcscCartApiUrl',
    }).then((res) => {
      if (res?.result?.status == 0) {
        message.error('商品不存在!');
        return;
      } else if (res?.result?.status == 1 || res?.result?.status == 4) {
        message.error('该商品已下架!');
        return;
      } else if (res?.result?.status == 2) {
        const msgUp =
          '商品' +
          res.result.productCode +
          '已有替代商品<a href="' +
          projectConfigVO.lcscItemUrl +
          '/' +
          res.result.productId +
          '.html" target="_blank" id="shopping_cart_info_link">' +
          res.result.productRepeatCode +
          '（查看替代商品）</a>，不接受订货';
        message.error(msgUp);
        return;
      } else {
        const result = res?.result;
        const priceList = result?.productPriceList || [];
        if (result?.productDiscountPriceList && result?.productDiscountPriceList?.length > 0) {
          result?.productDiscountPriceList?.forEach((item, index) => {
            if (result?.productDiscountPriceList?.length > priceList?.length) {
              priceList.push({ productPrice: null, startPurchasedNumber: null });
            }
            priceList[index].productPrice = item?.price;
            priceList[index].startPurchasedNumber = item?.spNumber;
          });
        }
        dataObj = { ...res?.result, entryType: 'item_details', btnType: 'mro-recommend' };
        dataObj.ratio = result.convesionRatio == 0 ? 1 : result.convesionRatio;
        dataObj.cartPrice = BasicRound(priceList[0]?.productPrice || 0, 6);
        dataObj.productPriceList = priceList;
        dataObj.minEncapsulationNumber = result.productMinEncapsulationNumber;
        setType(type);
        setBuyRow(dataObj);
        setIsModalOpen(true);
        setProductData(row);
      }
    });
    buriedPoint({
      cp: 'product_detail',
      ty: 'click',
      op: `${type === 'mro' ? 'MRO' : ''}recommend_cart`,
      data: { mainproductID: data?.productRecord?.productId, suggest: `${type === 'mro' ? 'MRO' : ''}recommend` },
    });
  };

  const RecommendItem = ({ type, item, index }) => {
    return (
      <li className="relative mt-[12px] flex h-[123px] w-[280px] mr-[10px] rounded-[8px] bg-[#F9F9F9] p-[20px] custom-group">
        {item?.recommendType === 'connector' && item?.productCode === data?.productRecord?.productCode && (
          <div className="absolute top-0 right-0 px-[4px] h-[20px] leading-[20px] bg-[#0093E6] text-[#fff] rounded-[0px_0px_0_4px]">
            当前商品
          </div>
        )}
        <div className="relative size-[60px]">
          <Image
            src={item?.breviaryImageUrl || defaultImg}
            alt={item?.productModel}
            className="size-[60px] object-contain"
            width={66}
            height={66}
            fallback={defaultImg}
          />
          {item?.recommendType === 'connector' && item?.connectorConfigDetailName && (
            <div className="absolute top-0 left-0 px-[4px] h-[20px] leading-[20px] bg-[#FF4960] text-[#fff] rounded-[0px_0px_4px_0px]">
              {item.connectorConfigDetailName}
            </div>
          )}
        </div>
        <div className="ml-[10px] flex-1 text-[12px]">
          <h3 className="w-[170px] font-bold text-[#0B1832] lc-text-hidden">
            <a
              href={`${projectConfigVO?.lcscItemUrl}/${item?.productId}.html?comefrom=buyAndbuy`}
              target="_blank"
              title={item.productName}
              data-spm={index + 1}
              onClick={() =>
                buriedPoint({
                  cp: 'product_detail',
                  ty: 'click',
                  op: `${type === 'mro' ? 'MRO' : ''}recommend_detail`,
                  data: {
                    mainproductID: data?.productRecord?.productId,
                    suggest: `${type === 'mro' ? 'MRO' : ''}recommend`,
                  },
                })
              }
            >
              {item.productName}
            </a>
          </h3>
          <dl>
            <div className="mt-[4px] flex">
              <dt className="text-[#7A8599]">品牌</dt>
              <dd className="ml-[10px] w-[130px] text-[#334463] lc-text-hidden">
                {item?.brandName || item?.productGradePlateName || item?.productGradePlateNameWithoutHighlight}
              </dd>
            </div>
            <div className="mt-[4px] flex">
              <dt className="text-[#7A8599]">封装</dt>
              <dd className="ml-[10px] w-[130px] text-[#334463] lc-text-hidden">{item?.encapsulationModel}</dd>
            </div>
          </dl>
          <div className="mt-[10px] flex items-center justify-between text-[#000000] custom-group-hover-text-red">
            {(item?.productDiscountPriceList || []).length > 0 ? (
              <span className="text-[14px]">
                ￥{item?.productDiscountPriceList[item?.productDiscountPriceList?.length - 1]?.price}
              </span>
            ) : (
              <span className="text-[14px]">
                ￥{item?.productPriceList[item?.productPriceList?.length - 1]?.productPrice}
              </span>
            )}
            <SvgIcon
              app="item"
              iconName="cart"
              className="w-[16px] h-[17px] cursor-pointer"
              onClick={() => clickGoBuy(item, type)}
            />
          </div>
        </div>
      </li>
    );
  };

  return (
    <>
      {recommendList.length > 0 && (
        <section
          data-spm="itj"
          ref={productRecommendRef}
          id="productRecommendTabItem"
          className="mt-[20px] mx-auto w-[1200px] rounded-[20px] bg-[#FFFFFF] px-[20px] py-[20px]"
        >
          <h2 className="w-[100%] text-[18px] text-[#0b1832] font-bold not-italic">买了又买</h2>
          <ul className="flex flex-wrap justify-start">
            {recommendList?.map((item, index) => (
              <RecommendItem type="electronic" item={item} index={index} key={index} />
            ))}
          </ul>
        </section>
      )}
      {mroRecommendList.length > 0 && (
        <section
          data-spm="mtj"
          ref={mroProductRecommendRef}
          id="productRecommendTabItem"
          className="mt-[20px] mx-auto w-[1200px] rounded-[20px] bg-[#FFFFFF] px-[20px] py-[20px]"
        >
          <h2 className="w-[100%] text-[18px] text-[#0b1832] font-bold not-italic">精选工业品推荐</h2>
          <ul className="flex flex-wrap justify-start">
            {mroRecommendList?.map((item, index) => (
              <RecommendItem type="mro" item={item} index={index} key={index} />
            ))}
          </ul>
        </section>
      )}
      {isModalOpen && (
        <GoBuyDialog
          type={type}
          row={buyRow}
          productData={productData}
          isModalOpen={isModalOpen}
          setIsModalOpen={(val) => setIsModalOpen(val)}
        />
      )}
    </>
  );
}
